import { FormattedMessage, useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import {
  Checkbox,
  Col,
  Form,
  Input,
  notification,
  Row,
  Skeleton,
  Typography,
} from 'antd';

import { useDefaultFormErrorNotification, useWindowSize } from '@/hooks';

import {
  PartItemFieldOptions,
  UpdatePartConfigurationProfileRequest,
} from '@/core/domain/stock/Configuration/Configuration.types';
import {
  usePartConfigurationProfile,
  useUpdatePartConfigurationProfile,
} from '@/core/domain/stock/Configuration/Configuration.hooks';

import { PageProps } from '@/types/PageProps';
import { SaveButton } from '@/components/_common';

const { Title, Paragraph } = Typography;

function PartsConfigurationPage({ route }: PageProps) {
  const intl = useIntl();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const [form] = Form.useForm<UpdatePartConfigurationProfileRequest>();
  const { width } = useWindowSize();

  const { data, isFetching } = usePartConfigurationProfile();

  const mutation = useUpdatePartConfigurationProfile();

  const submit = async () => {
    try {
      const values = await form.validateFields();

      await mutation.mutateAsync(values);

      notification.success({
        message: intl.formatMessage({
          id: 'settings.partsConfiguration.update.success',
        }),
        description: intl.formatMessage({
          id: 'settings.partsConfiguration.update.success.description',
        }),
      });
    } catch (err) {
      sendFormErrorNotification(err);
    }
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />
      <div className="page">
        <div className="page-header">
          <Title level={4}>
            <FormattedMessage id={route.name} />
          </Title>

          {!(width < 992) && (
            <SaveButton
              type="primary"
              onClick={submit}
              loading={mutation.isLoading}
            >
              <FormattedMessage id="commons.entity.save" />
            </SaveButton>
          )}
        </div>

        <Paragraph style={{ marginTop: '16px' }}>
          <FormattedMessage id="settings.partsConfiguration.description" />
        </Paragraph>

        <Skeleton active loading={isFetching}>
          <Form form={form} style={{ marginTop: '24px' }}>
            <Row gutter={[16, 12]}>
              {data?.result.map((item, i) => (
                <Col span={24} lg={12} key={item.field}>
                  <Form.Item
                    hidden
                    name={['fields', i, 'field']}
                    initialValue={item.field}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    noStyle
                    name={['fields', i, 'active']}
                    initialValue={item.active}
                    valuePropName="checked"
                  >
                    <Checkbox>
                      <FormattedMessage
                        id={`settings.partsConfiguration.${
                          PartItemFieldOptions[item.field]
                        }`}
                      />
                    </Checkbox>
                  </Form.Item>
                </Col>
              ))}
            </Row>
          </Form>
        </Skeleton>

        {width < 992 && (
          <SaveButton
            block
            type="primary"
            onClick={submit}
            loading={mutation.isLoading}
            style={{ marginTop: '20px' }}
          >
            <FormattedMessage id="commons.entity.save" />
          </SaveButton>
        )}
      </div>
    </>
  );
}

export default PartsConfigurationPage;
